import React, { useEffect, useState } from "react";
import { billingMe, billingCheckoutSession, billingPortal, billingVerifySession } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Crown, CheckCircle, X, ArrowRight, Receipt, Sparkle, CreditCard } from "@phosphor-icons/react";

function money(cents) { return `$${(cents / 100).toFixed(2)}`; }

export default function BillingView() {
  const { user, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("monthly");

  async function load() {
    try {
      const d = await billingMe();
      setInfo(d);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }

  useEffect(() => {
    load();
    // Check for billing_session in URL (post-checkout return)
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("billing_session");
    const canceled = params.get("billing_cancel");
    if (sessionId) {
      (async () => {
        try {
          await billingVerifySession(sessionId);
          await refresh();
          await load();
        } catch (e) { console.error(e); }
        window.history.replaceState({}, "", window.location.pathname);
      })();
    } else if (canceled) {
      setError("Checkout canceled — your plan was not changed.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [refresh]);

  async function startCheckout(plan) {
    setBusy(true);
    setError("");
    try {
      const { url } = await billingCheckoutSession(plan);
      if (url) window.location.href = url;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    try {
      const { url } = await billingPortal();
      if (url) window.location.href = url;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setBusy(false);
    }
  }

  const isPro = info?.is_pro || user?.is_pro;
  const status = info?.subscription_status || "";
  const grandfathered = status === "grandfathered";

  const monthlyCents = info?.catalog?.monthly?.cents ?? 999;
  const yearlyCents = info?.catalog?.yearly?.cents ?? 9999;
  const yearMonthlyEq = (yearlyCents / 12).toFixed(2);
  const yearSavingsPct = Math.round((1 - yearlyCents / (monthlyCents * 12)) * 100);

  return (
    <div className="view-grid" data-testid="billing-view">
      {/* Current plan banner */}
      <div className="panel">
        <div className="panel-header">
          <Crown size={18} weight="bold" />
          <span>YOUR PLAN</span>
        </div>
        <div className="panel-body">
          <div className="plan-row">
            <div className="plan-row-left">
              <div className={`plan-badge ${isPro ? "plan-badge--pro" : "plan-badge--free"}`} data-testid="current-plan-badge">
                {isPro ? <><Crown size={14} weight="fill" /> PRO</> : "FREE"}
              </div>
              <div>
                <div className="plan-row-status">
                  {grandfathered ? "Grandfathered (no charge)" : status ? `Status: ${status}` : "No subscription"}
                </div>
                {info?.current_period_end && (
                  <div className="plan-row-meta">
                    {info.cancel_at_period_end ? "Ends" : "Renews"}: {new Date(info.current_period_end).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            {info?.stripe_customer_id && (
              <button className="btn btn--outline" onClick={openPortal} disabled={busy} data-testid="open-portal-btn">
                <Receipt size={14} weight="bold" /> MANAGE BILLING
              </button>
            )}
          </div>
          {error && <div className="form-report form-report--err" data-testid="billing-error">{error}</div>}
        </div>
      </div>

      {/* Pricing cards */}
      <div className="pricing-cards">
        <div className="pricing-card pricing-card--free" data-testid="pricing-free">
          <div className="pricing-card-head">
            <div className="pricing-tier">FREE</div>
            <div className="pricing-amount"><span className="pricing-cents">$0</span><span className="pricing-period">/forever</span></div>
          </div>
          <ul className="pricing-features">
            <li><CheckCircle size={14} weight="bold" /> 1 workspace</li>
            <li><CheckCircle size={14} weight="bold" /> Full condition library (597 codes)</li>
            <li><CheckCircle size={14} weight="bold" /> Claim Tracker + evidence vault</li>
            <li><CheckCircle size={14} weight="bold" /> Statement builder</li>
            <li><X size={14} weight="bold" className="pricing-cross" /> Branded PDF (watermarked)</li>
            <li><X size={14} weight="bold" className="pricing-cross" /> Backups & restore</li>
            <li><X size={14} weight="bold" className="pricing-cross" /> Unlimited workspaces</li>
          </ul>
          {!isPro && <button className="btn btn--outline pricing-cta" disabled data-testid="current-free-btn">CURRENT PLAN</button>}
        </div>

        <div className={`pricing-card pricing-card--pro ${selectedPlan === "monthly" ? "pricing-card--selected" : ""}`} data-testid="pricing-monthly">
          <div className="pricing-popular"><Sparkle size={10} weight="fill" /> POPULAR</div>
          <div className="pricing-card-head">
            <div className="pricing-tier">PRO MONTHLY</div>
            <div className="pricing-amount"><span className="pricing-cents">{money(monthlyCents)}</span><span className="pricing-period">/month</span></div>
          </div>
          <ul className="pricing-features">
            <li><CheckCircle size={14} weight="bold" /> Everything in Free, plus:</li>
            <li><CheckCircle size={14} weight="bold" /> <strong>Unlimited workspaces</strong></li>
            <li><CheckCircle size={14} weight="bold" /> <strong>Clean branded PDF</strong> (no watermark)</li>
            <li><CheckCircle size={14} weight="bold" /> Automatic backups + restore</li>
            <li><CheckCircle size={14} weight="bold" /> Priority feature access</li>
            <li><CheckCircle size={14} weight="bold" /> Cancel anytime</li>
          </ul>
          {isPro ? (
            <button className="btn btn--success pricing-cta" disabled data-testid="current-pro-monthly-btn"><CheckCircle size={14} weight="bold" /> ACTIVE</button>
          ) : (
            <button className="btn btn--accent pricing-cta" onClick={() => startCheckout("monthly")} disabled={busy} data-testid="checkout-monthly-btn">
              <CreditCard size={14} weight="bold" /> {busy ? "REDIRECTING..." : "SUBSCRIBE MONTHLY"}
            </button>
          )}
        </div>

        <div className={`pricing-card pricing-card--pro pricing-card--yearly ${selectedPlan === "yearly" ? "pricing-card--selected" : ""}`} data-testid="pricing-yearly">
          {yearSavingsPct > 0 && <div className="pricing-popular pricing-popular--save"><Sparkle size={10} weight="fill" /> SAVE {yearSavingsPct}%</div>}
          <div className="pricing-card-head">
            <div className="pricing-tier">PRO YEARLY</div>
            <div className="pricing-amount"><span className="pricing-cents">{money(yearlyCents)}</span><span className="pricing-period">/year</span></div>
            <div className="pricing-subprice">${yearMonthlyEq}/month equivalent</div>
          </div>
          <ul className="pricing-features">
            <li><CheckCircle size={14} weight="bold" /> Everything in Pro Monthly</li>
            <li><CheckCircle size={14} weight="bold" /> <strong>~17% savings</strong> vs monthly</li>
            <li><CheckCircle size={14} weight="bold" /> Locked-in price for 12 months</li>
            <li><CheckCircle size={14} weight="bold" /> Cancel anytime, no fee</li>
          </ul>
          {isPro ? (
            <button className="btn btn--success pricing-cta" disabled data-testid="current-pro-yearly-btn"><CheckCircle size={14} weight="bold" /> ACTIVE</button>
          ) : (
            <button className="btn btn--accent pricing-cta" onClick={() => startCheckout("yearly")} disabled={busy} data-testid="checkout-yearly-btn">
              <CreditCard size={14} weight="bold" /> {busy ? "REDIRECTING..." : "SUBSCRIBE YEARLY"}
            </button>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-body">
          <p className="panel-desc" style={{ margin: 0 }}>
            <strong>Secure payment by Stripe.</strong> We never see your card. Subscriptions can be canceled anytime
            from the "Manage Billing" portal. Refunds are honored per Stripe terms.
          </p>
        </div>
      </div>
    </div>
  );
}
